import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useTranslation } from "react-i18next"

import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useTheme } from "hooks"

import { TYPE_REQUEST_CANCEL, TYPE_REQUEST, BIKE_SOLD } from "utils/constants"
import { isNotEmpty } from "utils"
import { getLanguage } from "utils/i18n"
import dateService from "services/date"
import { Markdown } from "components/Elements/Markdown"

import { TimelineSteps } from "../BackAndBuyBike/Partials"
import { getSteps } from "./Partials"
import { Bike, ErrorResponse } from "models"

type BikePortabilityProps = {
	bikeAvailableForPortability: Bike
}

export const BikePortability: React.FC<BikePortabilityProps> = ({
	bikeAvailableForPortability,
}) => {
	const { t } = useTranslation()
	const { alert } = useAlert()
	const { currentTheme } = useTheme()
	const defaultCurrentLanguage = getLanguage()

	const [bike, setBike] = React.useState(null)

	// Add 1 day to the requested date
	const bikePortabilityRequestExpiredDate =
		isNotEmpty(bike) &&
		dateService.addDays(dateService.getTimestamp(bike?.modify_date)?.toString(), 1)
	// Compare expired request date with the current date
	const isExpiredRequest =
		isNotEmpty(bike) && new Date().getTime() > Number(bikePortabilityRequestExpiredDate)

	const isBikeStatusAuthorizePortability = bikeAvailableForPortability?.bike_status === BIKE_SOLD

	const currentRequestSteps = React.useMemo(() => {
		if (isNotEmpty(bike)) {
			// We update the timeline (steps) when bike is updated
			const neededSteps = [
				"SEND",
				"WAITING",
				...(() => {
					if (isExpiredRequest) return ["EXPIRED"]
					return ["ACCEPTED"]
				})(),
			]
			return getSteps(bike).filter((step) => neededSteps.includes(step.id))
		}
	}, [bike, defaultCurrentLanguage]) // eslint-disable-line

	// Handle fetch bike
	// -------------------------------
	const handleBikeFetch = async () => {
		try {
			const { res, getError } = await BikeApi.getBikePortability(
				bikeAvailableForPortability.bike_id,
				currentTheme.id_api
			)
			if (getError()) throw getError()

			setBike(res)
		} catch (error) {
			const erroMsg = (error as ErrorResponse)?.message?.toString()
			// Do not display alert if RessourceNotFoundException => message = "You did not request a transfer code for this bike."
			const isNoRequestForThisBike =
				(error as ErrorResponse).code === "RessourceNotFoundException"
			if (erroMsg && !isNoRequestForThisBike) alert("error", erroMsg)
		}
	}

	React.useEffect(() => {
		if (isNotEmpty(bikeAvailableForPortability)) {
			setBike(null) // reset bike info if user use search form without reload page
			handleBikeFetch()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bikeAvailableForPortability])

	const handleSubmit = async (event, portabilityContext) => {
		event.preventDefault()
		try {
			const portabilityPayload = {
				bike_id: bikeAvailableForPortability.bike_id,
				// The currentTheme contains the name of the current operator with id_api
				// Warning an entity can have more than 1 operator => we cannot rely on useAuth for set the correct operator
				operator: currentTheme.id_api,
				context: portabilityContext,
			}

			const { getError } = await BikeApi.portabilityRequest(portabilityPayload)
			if (getError()) throw getError()

			if (portabilityContext === TYPE_REQUEST) {
				// Request sended
				handleBikeFetch()
				alert("success", t("back_in_store.modal_success_message", { ns: "bike" }))
			} else {
				// Request cancelled
				alert("success", t("portability.portability_cancelled", { ns: "bike" }))
				setBike(null)
			}
		} catch (error) {
			const erroMsg = (error as ErrorResponse)?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
		}
	}

	if (!isNotEmpty(bikeAvailableForPortability)) return <></>

	return (
		<div className="c-card p-5 md:p-12 w-full mt-5">
			{bike && (
				<>
					<h2 className="c-h2 font-semibold mb-5 uppercase">
						{t("portability.title_on_going", { ns: "bike" })}
					</h2>

					<div className="mt-1 mb-5">
						<Markdown>{t("portability.description_on_going", { ns: "bike" })}</Markdown>
						{!isBikeStatusAuthorizePortability && (
							<p className="mt-2 text-danger">
								{t("portability.not_allowed", { ns: "bike" })}
							</p>
						)}
					</div>

					{currentRequestSteps && <TimelineSteps steps={currentRequestSteps} />}

					<div
						className={clsx("flex justify-between mt-5", { "flex-col": isMobileOnly })}
					>
						<button
							data-cy="cancel-request-port"
							type="button"
							className="c-btn c-btn--primary c-btn--m px-7 mt-5"
							onClick={(e) => handleSubmit(e, TYPE_REQUEST_CANCEL)}
						>
							{t("back_and_buy.cancel_button", { ns: "bike" })}
						</button>

						<button
							data-cy="submit-request-port"
							type="button"
							className="c-btn c-btn--primary c-btn--m px-7 mt-5"
							onClick={(e) => handleSubmit(e, TYPE_REQUEST)}
							disabled={!isBikeStatusAuthorizePortability}
						>
							{t("back_and_buy.renew_button", { ns: "bike" })}
						</button>
					</div>
				</>
			)}

			{!bike && (
				<>
					<h2 className="c-h2 font-semibold mb-5 uppercase">
						{t("portability.title", { ns: "bike" })}
					</h2>

					<div className="mt-1 mb-5">
						<Markdown>{t("portability.description", { ns: "bike" })}</Markdown>
						{!isBikeStatusAuthorizePortability && (
							<p className="mt-2 text-danger">
								{t("portability.not_allowed", { ns: "bike" })}
							</p>
						)}
					</div>

					<button
						data-cy="handle-request-port"
						type="button"
						className="c-btn c-btn--primary c-btn--m px-7 mt-5"
						onClick={(e) => handleSubmit(e, TYPE_REQUEST)}
						disabled={!isBikeStatusAuthorizePortability}
					>
						{t("portability.button", { ns: "bike" })}
					</button>
				</>
			)}
		</div>
	)
}
